/**
 * Form Builder — lets an agency_admin edit the agency-default form spec.
 * Left pane: structural editor (drag to reorder, edit labels, toggle
 * required/hidden, add/remove fields & sections, edit options & marketing copy).
 * Right pane: live preview rendered by the shared renderer (lib/form-render.ts),
 * so what they build is exactly what employees will see.
 *
 * State model: one working FormSpec. Text edits update state + preview only
 * (no editor rebuild, so focus is never lost). Structural edits rebuild both.
 */
import { api, ApiError } from '../lib/api.js';
import { esc, type Branding } from '../lib/brand.js';
import { loading } from '../lib/shell.js';
import { toast } from '../lib/toast.js';
import { icon } from '../lib/icons.js';
import {
  renderSections, renderWhyItems,
  type FormSpec, type SpecSection, type SpecField, type FieldType,
} from '../lib/form-render.js';

interface BuilderAgency { name: string; branding: Branding }

interface BuilderResult { spec: FormSpec; customized: boolean }
interface BuilderApi {
  load: () => Promise<BuilderResult>;
  save: (spec: FormSpec) => Promise<BuilderResult>;
  reset: () => Promise<BuilderResult>;
}
interface BuilderConfig {
  agency: BuilderAgency;
  allowSections: boolean;          // agency default: true; per-form: false
  resetLabel: string;
  resetConfirm: string;
  status: { customized: string; standard: string };
  api: BuilderApi;
}

const TYPE_LABEL: Record<FieldType, string> = {
  text: 'Text', email: 'Email', tel: 'Phone', date: 'Date', 'checkbox-group': 'Checkboxes', consent: 'Consent',
};
const ADDABLE_TYPES: FieldType[] = ['text', 'email', 'tel', 'date', 'checkbox-group'];
const GRIP = '<span class="fb-grip" data-drag-handle title="Drag to reorder">⋮⋮</span>';

export function formBuilderBody(): string {
  return `
  <div class="fb">
    <div class="fb-bar">
      <div class="fb-status" id="fbStatus"></div>
      <div class="row" style="gap:8px">
        <button class="btn ghost sm" id="fbReset">Reset to Apex default</button>
        <button class="btn" id="fbSave" disabled>Save changes</button>
      </div>
    </div>
    <div class="fb-grid">
      <div class="fb-editor" id="fbEditor">${loading()}</div>
      <div class="fb-preview-wrap">
        <div class="fb-preview-label">Live preview</div>
        <div class="fb-preview" id="fbPreview"></div>
      </div>
    </div>
  </div>`;
}

/** Agency-default builder (Settings → Form Builder). */
export function wireFormBuilder(root: HTMLElement, agency: BuilderAgency): void {
  mountFormBuilder(root, {
    agency, allowSections: true, resetLabel: 'Reset to Apex default',
    resetConfirm: 'Reset to the standard Apex form? This discards your agency\'s customizations.',
    status: { customized: 'Using your customized form.', standard: 'Using the standard Apex form.' },
    api: {
      load: () => api.get<BuilderResult>('/api/agency/form-default'),
      save: (spec) => api.put<BuilderResult>('/api/agency/form-default', { spec }),
      reset: () => api.del<BuilderResult>('/api/agency/form-default'),
    },
  });
}

/** Per-form override builder (Forms → Customize fields). Seeded from the agency
 *  default; saved as a diff patch on the form. */
export function wirePerFormBuilder(root: HTMLElement, agency: BuilderAgency, formId: string): void {
  mountFormBuilder(root, {
    agency, allowSections: false, resetLabel: 'Reset to agency default',
    resetConfirm: 'Remove this form\'s customizations and use your agency default?',
    status: { customized: 'This form has custom fields (layered on your agency default).', standard: 'This form uses your agency default.' },
    api: {
      load: () => api.get<BuilderResult>(`/api/forms/${formId}/spec`),
      save: (spec) => api.put<BuilderResult>(`/api/forms/${formId}/spec`, { spec }),
      reset: () => api.del<BuilderResult>(`/api/forms/${formId}/spec`),
    },
  });
}

function mountFormBuilder(root: HTMLElement, cfg: BuilderConfig): void {
  const agency = cfg.agency;
  const editorEl = root.querySelector('#fbEditor') as HTMLElement;
  const previewEl = root.querySelector('#fbPreview') as HTMLElement;
  const statusEl = root.querySelector('#fbStatus') as HTMLElement;
  const saveBtn = root.querySelector('#fbSave') as HTMLButtonElement;
  const resetBtn = root.querySelector('#fbReset') as HTMLButtonElement;
  resetBtn.textContent = cfg.resetLabel;

  let spec: FormSpec = { version: 1, sections: [], content: { whyChooseUs: [] } };
  let customized = false;
  let dirty = false;

  /* -------------------------------- helpers -------------------------------- */
  const allFieldKeys = () => new Set(spec.sections.flatMap((s) => s.fields.map((f) => f.key)));
  const allSectionIds = () => new Set(spec.sections.map((s) => s.id));
  const findField = (key: string): SpecField | undefined => spec.sections.flatMap((s) => s.fields).find((f) => f.key === key);
  const sectionOf = (id: string) => spec.sections.find((s) => s.id === id);
  const sectionRemovable = (s: SpecSection) => s.fields.every((f) => f.binding === 'custom');

  function genKey(prefix: string, taken: Set<string>): string {
    let i = 1; let k = `${prefix}_${i}`;
    while (taken.has(k)) k = `${prefix}_${++i}`;
    return k;
  }
  function resequence(): void {
    spec.sections.forEach((s, i) => { s.order = i; s.fields.forEach((f, j) => { f.order = j; }); });
  }
  function markDirty(): void {
    dirty = true;
    saveBtn.disabled = false;
    updateStatus();
  }
  function updateStatus(): void {
    const base = customized ? cfg.status.customized : cfg.status.standard;
    statusEl.innerHTML = dirty ? `<span class="fb-dirty">● Unsaved changes</span>` : `<span class="muted">${esc(base)}</span>`;
  }

  /* --------------------------------- render -------------------------------- */
  function renderAll(): void { resequence(); renderEditor(); renderPreview(); }

  function renderPreview(): void {
    const ctx = { agencyName: agency.name, companyName: 'Sample Company', branding: agency.branding || {} };
    previewEl.innerHTML = `
      <div class="fb-preview-inner">
        <div class="pub-banner"><h1>Employee Benefits Interest Form</h1>
          <p>Prepared for Sample Company · Rated Top For Most Affordable Workplace Benefits</p></div>
        <div class="card"><div class="card-body">${renderSections(spec, ctx)}
          <button class="btn block" type="button" disabled style="margin-top:18px">Submit my interest form</button>
        </div></div>
        <aside class="why"><h3>Why Choose Us</h3><ul>${renderWhyItems(spec)}</ul></aside>
      </div>`;
  }

  function fieldRow(f: SpecField): string {
    const lockCtl = (dis: boolean) => (dis ? 'disabled' : '');
    // Core fields (name, consent, locked) are always required + visible — server enforces
    // the same invariants, so disabling here avoids a "it came back on save" surprise.
    const core = f.type === 'consent' || !!f.locked || f.key === 'first_name' || f.key === 'last_name';
    const reqDisabled = core;
    const hideDisabled = core;
    const del = f.binding === 'custom'
      ? `<button class="btn ghost xs" data-action="del-field" data-key="${esc(f.key)}">Remove</button>`
      : (f.locked ? `<span class="fb-lock" title="Server-managed">${icon('lock')}</span>` : '');
    return `
      <div class="fb-field" data-drag="field" data-fld-key="${esc(f.key)}">
        <div class="fb-field-head">
          ${GRIP}
          <input class="fb-inp fb-fld-label" data-fld-label="${esc(f.key)}" value="${esc(f.label)}" placeholder="Field label" />
          <span class="fb-badge">${TYPE_LABEL[f.type]}${f.binding === 'custom' ? ' · custom' : ''}</span>
        </div>
        <div class="fb-field-ctl">
          <label class="fb-chk"><input type="checkbox" data-fld-required="${esc(f.key)}" ${f.required ? 'checked' : ''} ${lockCtl(reqDisabled)}/> Required</label>
          <label class="fb-chk"><input type="checkbox" data-fld-hidden="${esc(f.key)}" ${f.hidden ? 'checked' : ''} ${lockCtl(hideDisabled)}/> Hidden</label>
          ${del}
        </div>
        ${f.type === 'checkbox-group' ? optionsEditor(f) : ''}
      </div>`;
  }

  function optionsEditor(f: SpecField): string {
    const opts = (f.options ?? []).map((o, idx) => `
      <div class="fb-opt">
        <input class="fb-inp" data-opt-label="${esc(f.key)}|${idx}" value="${esc(o.label)}" placeholder="Option label" />
        <button class="btn ghost xs" data-action="del-option" data-key="${esc(f.key)}" data-idx="${idx}">×</button>
      </div>`).join('');
    return `<div class="fb-opts"><div class="fb-opts-label">Options</div>${opts}
      <button class="btn ghost xs" data-action="add-option" data-key="${esc(f.key)}">+ Add option</button></div>`;
  }

  function sectionBlock(s: SpecSection): string {
    const removeBtn = cfg.allowSections && sectionRemovable(s)
      ? `<button class="btn ghost xs" data-action="del-section" data-sec="${esc(s.id)}">Remove section</button>` : '';
    const typeOpts = ADDABLE_TYPES.map((t) => `<option value="${t}">${TYPE_LABEL[t]}</option>`).join('');
    return `
      <div class="fb-sec card" data-drag="section" data-sec-id="${esc(s.id)}">
        <div class="fb-sec-head">
          ${GRIP}
          <input class="fb-inp fb-sec-title" data-sec-title="${esc(s.id)}" value="${esc(s.title)}" placeholder="Section title" />
          ${removeBtn}
        </div>
        <input class="fb-inp fb-sec-sub" data-sec-subtitle="${esc(s.id)}" value="${esc(s.subtitle ?? '')}" placeholder="Subtitle (optional)" />
        <div class="fb-fields" data-sec-id="${esc(s.id)}">${s.fields.map(fieldRow).join('')}</div>
        <div class="fb-add-field">
          <select class="fb-inp" data-add-type="${esc(s.id)}">${typeOpts}</select>
          <button class="btn ghost sm" data-action="add-field" data-sec="${esc(s.id)}">+ Add field</button>
        </div>
      </div>`;
  }

  function renderEditor(): void {
    const why = spec.content.whyChooseUs.map((w, idx) => `
      <div class="fb-why-row">
        <input class="fb-inp" data-why="${idx}" value="${esc(w)}" placeholder="Reason" />
        <button class="btn ghost xs" data-action="del-why" data-idx="${idx}">×</button>
      </div>`).join('');
    editorEl.innerHTML = `
      <div class="fb-sections" id="fbSections">${spec.sections.map(sectionBlock).join('')}</div>
      ${cfg.allowSections ? '<button class="btn secondary sm" data-action="add-section" style="margin:4px 0 20px">+ Add section</button>' : ''}
      <div class="fb-why card">
        <h3 style="margin:0 0 10px">Why Choose Us</h3>
        <div class="fb-why-list">${why}</div>
        <button class="btn ghost sm" data-action="add-why">+ Add reason</button>
      </div>`;
    wireEditor();
  }

  /* --------------------------------- wiring -------------------------------- */
  function wireEditor(): void {
    // Live text edits: state + preview only (keep editor DOM + focus intact).
    editorEl.oninput = (e) => {
      const t = e.target as HTMLElement;
      const ds = (t as HTMLInputElement).dataset;
      const val = (t as HTMLInputElement).value;
      if (ds.secTitle !== undefined) { const s = sectionOf(ds.secTitle); if (s) s.title = val; }
      else if (ds.secSubtitle !== undefined) { const s = sectionOf(ds.secSubtitle); if (s) s.subtitle = val; }
      else if (ds.fldLabel !== undefined) { const f = findField(ds.fldLabel); if (f) f.label = val; }
      else if (ds.optLabel !== undefined) {
        const [key, idxStr] = ds.optLabel.split('|'); const f = findField(key);
        const o = f?.options?.[Number(idxStr)]; if (o) o.label = val;
      } else if (ds.why !== undefined) { spec.content.whyChooseUs[Number(ds.why)] = val; }
      else return;
      markDirty(); renderPreview();
    };

    // Required / hidden toggles.
    editorEl.onchange = (e) => {
      const t = e.target as HTMLInputElement;
      if (t.dataset.fldRequired !== undefined) { const f = findField(t.dataset.fldRequired); if (f) f.required = t.checked; }
      else if (t.dataset.fldHidden !== undefined) { const f = findField(t.dataset.fldHidden); if (f) f.hidden = t.checked; }
      else return;
      markDirty(); renderPreview();
    };

    // Structural actions: mutate + full rebuild.
    editorEl.onclick = (e) => {
      const btn = (e.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
      if (!btn) return;
      const act = btn.dataset.action!;
      if (act === 'add-section') addSection();
      else if (act === 'del-section') delSection(btn.dataset.sec!);
      else if (act === 'add-field') addField(btn.dataset.sec!);
      else if (act === 'del-field') delField(btn.dataset.key!);
      else if (act === 'add-option') addOption(btn.dataset.key!);
      else if (act === 'del-option') delOption(btn.dataset.key!, Number(btn.dataset.idx));
      else if (act === 'add-why') { spec.content.whyChooseUs.push(''); markDirty(); renderAll(); }
      else if (act === 'del-why') { spec.content.whyChooseUs.splice(Number(btn.dataset.idx), 1); markDirty(); renderAll(); }
    };

    // Drag reorder — sections, and fields within each section.
    const sectionsEl = editorEl.querySelector('#fbSections') as HTMLElement;
    wireDrag(sectionsEl, '[data-drag="section"]', () => {
      const order = [...sectionsEl.querySelectorAll<HTMLElement>('[data-drag="section"]')].map((el) => el.dataset.secId!);
      spec.sections.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
      markDirty(); renderAll();
    });
    editorEl.querySelectorAll<HTMLElement>('.fb-fields').forEach((listEl) => {
      const s = sectionOf(listEl.dataset.secId!); if (!s) return;
      wireDrag(listEl, '[data-drag="field"]', () => {
        const order = [...listEl.querySelectorAll<HTMLElement>('[data-drag="field"]')].map((el) => el.dataset.fldKey!);
        s.fields.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
        markDirty(); renderAll();
      });
    });
  }

  /* ------------------------------- mutations ------------------------------- */
  function addSection(): void {
    const id = genKey('section', allSectionIds());
    spec.sections.push({ id, title: 'New section', order: spec.sections.length, fields: [] });
    markDirty(); renderAll();
  }
  function delSection(id: string): void {
    const s = sectionOf(id);
    if (!s || !sectionRemovable(s)) { toast('This section is built-in and can\'t be removed', 'err'); return; }
    spec.sections = spec.sections.filter((x) => x.id !== id);
    markDirty(); renderAll();
  }
  function addField(sectionId: string): void {
    const s = sectionOf(sectionId); if (!s) return;
    const type = (editorEl.querySelector(`[data-add-type="${sectionId}"]`) as HTMLSelectElement)?.value as FieldType;
    const key = genKey('field', allFieldKeys());
    const field: SpecField = { key, type, label: 'New field', required: false, hidden: false, order: s.fields.length, binding: 'custom' };
    if (type === 'checkbox-group') field.options = [{ key: 'option_1', label: 'Option 1' }];
    s.fields.push(field);
    markDirty(); renderAll();
  }
  function delField(key: string): void {
    const f = findField(key);
    if (!f || f.binding !== 'custom') { toast('Built-in fields can\'t be removed (hide them instead)', 'err'); return; }
    for (const s of spec.sections) s.fields = s.fields.filter((x) => x.key !== key);
    markDirty(); renderAll();
  }
  function addOption(key: string): void {
    const f = findField(key); if (!f) return;
    f.options ??= [];
    const taken = new Set(f.options.map((o) => o.key));
    f.options.push({ key: genKey('option', taken), label: 'New option' });
    markDirty(); renderAll();
  }
  function delOption(key: string, idx: number): void {
    const f = findField(key); if (!f?.options) return;
    if (f.options.length <= 1) { toast('A checkbox field needs at least one option', 'err'); return; }
    f.options.splice(idx, 1);
    markDirty(); renderAll();
  }

  /* --------------------------------- save ---------------------------------- */
  saveBtn.onclick = async () => {
    // Drop blank "Why Choose Us" lines so they don't render as empty bullets.
    spec.content.whyChooseUs = spec.content.whyChooseUs.map((w) => w.trim()).filter(Boolean);
    resequence();
    saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
    try {
      const r = await cfg.api.save(spec);
      spec = normalizeLoaded(r.spec); customized = r.customized; dirty = false;
      toast('Form saved');
      renderAll(); updateStatus();
    } catch (err) {
      saveBtn.disabled = false;
      toast(err instanceof ApiError ? err.message : 'Save failed', 'err');
    } finally {
      saveBtn.textContent = 'Save changes';
    }
  };

  resetBtn.onclick = async () => {
    if (!confirm(cfg.resetConfirm)) return;
    try {
      const r = await cfg.api.reset();
      spec = normalizeLoaded(r.spec); customized = r.customized; dirty = false;
      toast('Reset complete');
      renderAll(); updateStatus();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Reset failed', 'err');
    }
  };

  // Sort into display order on load so array order == render order.
  function normalizeLoaded(s: FormSpec): FormSpec {
    s.sections.sort((a, b) => a.order - b.order);
    s.sections.forEach((sec) => sec.fields.sort((a, b) => a.order - b.order));
    return s;
  }

  /* --------------------------------- load ---------------------------------- */
  (async () => {
    try {
      const r = await cfg.api.load();
      spec = normalizeLoaded(r.spec); customized = r.customized;
    } catch (err) {
      editorEl.innerHTML = `<div class="note">Could not load the form: ${esc(err instanceof ApiError ? err.message : 'error')}</div>`;
      return;
    }
    renderAll(); updateStatus();
  })();
}

/* --------------------------- native drag reorder --------------------------- */
function wireDrag(listEl: HTMLElement, selector: string, onReorder: () => void): void {
  let dragging: HTMLElement | null = null;
  listEl.querySelectorAll<HTMLElement>(selector).forEach((item) => {
    const handle = item.querySelector<HTMLElement>('[data-drag-handle]');
    if (!handle) return;
    handle.addEventListener('pointerdown', () => item.setAttribute('draggable', 'true'));
    handle.addEventListener('pointerup', () => item.removeAttribute('draggable'));
    item.addEventListener('dragstart', (e) => { dragging = item; item.classList.add('dragging'); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; });
    item.addEventListener('dragend', () => { item.classList.remove('dragging'); item.removeAttribute('draggable'); const moved = dragging; dragging = null; if (moved) onReorder(); });
  });
  listEl.addEventListener('dragover', (e) => {
    if (!dragging) return;
    e.preventDefault();
    const after = dragAfter(listEl, selector, e.clientY);
    if (after == null) listEl.appendChild(dragging);
    else if (after !== dragging) listEl.insertBefore(dragging, after);
  });
}
function dragAfter(listEl: HTMLElement, selector: string, y: number): HTMLElement | null {
  let closest: { offset: number; el: HTMLElement | null } = { offset: -Infinity, el: null };
  for (const el of listEl.querySelectorAll<HTMLElement>(`${selector}:not(.dragging)`)) {
    const box = el.getBoundingClientRect();
    const offset = y - box.top - box.height / 2;
    if (offset < 0 && offset > closest.offset) closest = { offset, el };
  }
  return closest.el;
}
